import { useCallback } from 'react'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getSeriesFrame,
  getUnavailableImageFrames,
  setSeriesFrame,
} from '../../../store/sessionDataSlice'
import { SvgChevronLeft, SvgChevronRight } from '../../../components/SvgImports'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'

interface ViewSelectorRootProps {
  $direction: 'right' | 'left'
}

const ViewSelectorRoot = styled.div<ViewSelectorRootProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 40px;
  margin-right: ${(props) => (props.$direction === 'right' ? '24px' : '0px')};
  margin-left: ${(props) => (props.$direction === 'left' ? '16px' : '0px')};

  svg {
    width: 24px;
    height: 24px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    margin-left: 0px;
    margin-right: 0px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

interface MainImageViewSelectorProps {
  direction: 'left' | 'right'
}

export function MainImageViewSelector({
  direction,
}: MainImageViewSelectorProps): JSX.Element {
  const dispatch = useAppDispatch()
  const seriesFrame = useAppSelector(getSeriesFrame)
  const unavailableImageFrames = useAppSelector(getUnavailableImageFrames)

  const frameIsAvailable = (seriesId: string, frameId: number): boolean => {
    return (
      -1 ===
      unavailableImageFrames.findIndex(
        (u) => u.seriesId === seriesId && u.frameId === frameId,
      )
    )
  }

  const viewAlternatives = [
    { series: 'EXTERIOR', frame: 1, key: 1 },
    { series: 'EXTERIOR', frame: 2, key: 2 },
    { series: 'EXTERIOR', frame: 3, key: 3 },
    { series: 'INTERIOR', frame: 1, key: 4 },
    { series: 'INTERIOR', frame: 2, key: 5 },
    { series: 'INTERIOR', frame: 3, key: 6 },
    { series: 'INTERIOR', frame: 4, key: 7 },
  ].filter((v) => frameIsAvailable(v.series, v.frame))

  const handleLeftOrRightClick = useCallback(
    (direction: number) => {
      if (!seriesFrame) {
        return
      }
      const { seriesId, frameId } = seriesFrame
      if (seriesId === '360') {
        // Should not happen, 360 has a dedicated component.
        console.warn('Unexpected viewSeries ' + seriesId)
        return
      }
      const i = viewAlternatives.findIndex(
        (view) => view.series === seriesId && view.frame === frameId,
      )
      let j = i + Math.round(direction)
      j = j < 0 ? viewAlternatives.length + j : j
      j = j % viewAlternatives.length
      const next = viewAlternatives[j]
      dispatch(setSeriesFrame({ seriesId: next.series, frameId: next.frame }))
    },
    [viewAlternatives, seriesFrame, dispatch],
  )

  return (
    <ViewSelectorRoot
      onClick={() => handleLeftOrRightClick(direction === 'right' ? 1 : -1)}
      $direction={direction}
    >
      {direction === 'right' ? <SvgChevronRight /> : <SvgChevronLeft />}
    </ViewSelectorRoot>
  )
}
